import { useState } from "react";
import { HeartHandshake, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { sendNoteToSelf } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export function VowLetter() {
  const [letter, setLetter] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sealed, setSealed] = useState(false);

  const seal = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendNoteToSelf(letter, email, "vow");
      setSealed(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-flame-soft border border-flame/15" data-testid="vow-letter">
      <div className="mx-auto max-w-4xl px-6 md:px-10 py-16 md:py-24">
        <p className="eyebrow !text-flame">Write your vows</p>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
          A letter to yourself
        </h2>
        <p className="mt-5 max-w-2xl text-neutral-600 leading-relaxed">
          Expressing yourself is full commitment. Write your vows here: a promise, an encouragement, a
          reminder of who you are becoming. It arrives in your own inbox, from you to you. Completely
          private, never stored, never shared.
        </p>

        <AnimatePresence mode="wait">
          {sealed ? (
            <motion.div
              key="sealed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-10 text-center py-10"
              data-testid="vow-success"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-flame">
                <Check size={22} />
              </span>
              <h3 className="mt-4 font-serif text-3xl text-ink">'Til death do us part.</h3>
              <p className="mt-2 text-sm text-neutral-600 max-w-sm mx-auto">
                Your vows are sealed and on their way to your inbox. Keep them close.
              </p>
              <button
                type="button"
                data-testid="vow-again-button"
                onClick={() => {
                  setLetter("");
                  setEmail("");
                  setSealed(false);
                }}
                className="mt-6 text-sm text-royal link-underline"
              >
                Write another letter
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={seal}
              className="mt-10 bg-white border border-neutral-200 p-8 md:p-10"
              data-testid="vow-form"
            >
              <Label htmlFor="vow-letter" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                Your vows
              </Label>
              <Textarea
                id="vow-letter"
                data-testid="vow-letter-input"
                required
                rows={7}
                value={letter}
                onChange={(e) => setLetter(e.target.value)}
                className="field resize-none"
                placeholder="Dear me, I vow to…"
              />
              <div className="mt-7">
                <Label htmlFor="vow-email" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                  Deliver to
                </Label>
                <Input
                  id="vow-email"
                  type="email"
                  data-testid="vow-email-input"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field"
                  placeholder="you@example.com"
                />
              </div>
              <button
                type="submit"
                data-testid="vow-submit-button"
                disabled={sending}
                className="mt-8 w-full bg-flame text-white py-4 text-sm tracking-wide hover:bg-royal transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                <HeartHandshake size={16} /> {sending ? "Sealing…" : "Seal my vows"}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
